import { useEffect, useState, type FormEvent } from "react";

import {
  createOutput,
  patchOutput,
  type OutputInput,
} from "../../api/outputs.js";
import { Button } from "../../design-system/button.js";
import {
  Field,
  NumberInput,
  Select,
  Switch,
  TextInput,
} from "../../design-system/form.js";
import { Modal } from "../../design-system/modal.js";
import { Notice } from "../../design-system/surfaces.js";
import type { Output, OutputCodec, OutputVisibility } from "../../api/types.js";

/**
 * One delivery, created or changed.
 *
 * On create, every field left blank is sent as nothing at all, so what lands in the
 * row is the schema's own baseline — this form holds no default codec, bitrate,
 * sample rate or channel count, because the row is the sole authority for what a feed
 * publishes and a copy of it here would be a second one.
 *
 * On edit, only what the operator actually changed is sent.
 */
const CODECS: OutputCodec[] = ["mp3", "aac", "opus", "vorbis"];

interface Draft {
  codec: string;
  bitrate: string;
  sampleRate: string;
  channels: string;
  mountPath: string;
  host: string;
  port: string;
  publishedUrl: string;
  visibility: string;
  enabled: boolean;
}

const EMPTY: Draft = {
  codec: "",
  bitrate: "",
  sampleRate: "",
  channels: "",
  mountPath: "",
  host: "",
  port: "",
  publishedUrl: "",
  visibility: "",
  enabled: true,
};

function draftOf(output: Output | null): Draft {
  if (!output) return EMPTY;
  return {
    codec: output.codec,
    bitrate: String(output.bitrateKbps),
    sampleRate: String(output.sampleRateHz),
    channels: String(output.channels),
    mountPath: output.mountPath,
    host: output.host,
    port: String(output.port),
    publishedUrl: output.publishedUrl,
    visibility: output.visibility,
    enabled: output.enabled,
  };
}

export function OutputDialog({
  feedId,
  onClose,
  onSaved,
  open,
  output,
}: {
  feedId: string;
  onClose: () => void;
  onSaved: () => void;
  open: boolean;
  output: Output | null;
}) {
  const [draft, setDraft] = useState<Draft>(() => draftOf(output));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const editing = output !== null;

  useEffect(() => {
    if (open) {
      setDraft(draftOf(output));
      setError(null);
    }
  }, [open, output]);

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const original = draftOf(output);
    const input: OutputInput = {};
    if (draft.codec !== original.codec && draft.codec !== "") {
      input.codec = draft.codec as OutputCodec;
    }
    if (draft.bitrate !== original.bitrate && draft.bitrate !== "") {
      input.bitrate_kbps = Number(draft.bitrate);
    }
    if (draft.sampleRate !== original.sampleRate && draft.sampleRate !== "") {
      input.sample_rate_hz = Number(draft.sampleRate);
    }
    if (draft.channels !== original.channels && draft.channels !== "") {
      input.channels = Number(draft.channels);
    }
    if (draft.mountPath !== original.mountPath && draft.mountPath !== "") {
      input.mount_path = draft.mountPath.trim();
    }
    if (draft.host !== original.host && draft.host !== "") {
      input.host = draft.host.trim();
    }
    if (draft.port !== original.port && draft.port !== "") {
      input.port = Number(draft.port);
    }
    if (
      draft.publishedUrl !== original.publishedUrl &&
      draft.publishedUrl !== ""
    ) {
      input.published_url = draft.publishedUrl.trim();
    }
    if (draft.visibility !== original.visibility && draft.visibility !== "") {
      input.visibility = draft.visibility as OutputVisibility;
    }
    if (draft.enabled !== original.enabled) input.enabled = draft.enabled;

    setBusy(true);
    setError(null);
    const request = editing
      ? patchOutput(feedId, output.id, input)
      : createOutput(feedId, input);
    request
      .then(() => {
        onSaved();
        onClose();
      })
      .catch((cause: unknown) => {
        setError(
          cause instanceof Error
            ? cause.message
            : "The delivery was not saved.",
        );
      })
      .finally(() => {
        setBusy(false);
      });
  }

  return (
    <Modal
      description={
        editing ? "Format and address." : "Leave blank to use the default."
      }
      onClose={onClose}
      open={open}
      title={editing ? "Edit delivery" : "Add a delivery"}
      width="36rem"
    >
      <form className="flex flex-col gap-5" noValidate onSubmit={submit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field htmlFor="output-codec" label="Codec">
            <Select
              id="output-codec"
              onChange={(event) => {
                set("codec", event.target.value);
              }}
              value={draft.codec}
            >
              <option value="">Baseline</option>
              {CODECS.map((codec) => (
                <option key={codec} value={codec}>
                  {codec.toUpperCase()}
                </option>
              ))}
            </Select>
          </Field>

          <Field htmlFor="output-bitrate" label="Bitrate">
            <NumberInput
              id="output-bitrate"
              max={2048}
              min={8}
              onChange={(event) => {
                set("bitrate", event.target.value);
              }}
              placeholder="baseline"
              unit="kbps"
              value={draft.bitrate}
            />
          </Field>

          <Field htmlFor="output-sample-rate" label="Sample rate">
            <NumberInput
              id="output-sample-rate"
              max={192_000}
              min={8_000}
              onChange={(event) => {
                set("sampleRate", event.target.value);
              }}
              placeholder="baseline"
              unit="Hz"
              value={draft.sampleRate}
            />
          </Field>

          <Field htmlFor="output-channels" label="Channels">
            <Select
              id="output-channels"
              onChange={(event) => {
                set("channels", event.target.value);
              }}
              value={draft.channels}
            >
              <option value="">Baseline</option>
              <option value="1">Mono</option>
              <option value="2">Stereo</option>
            </Select>
          </Field>

          <Field
            hint="The path on the streaming server, e.g. /radio.mp3."
            htmlFor="output-mount"
            label="Mount path"
          >
            <TextInput
              id="output-mount"
              onChange={(event) => {
                set("mountPath", event.target.value);
              }}
              placeholder="/late-night.mp3"
              value={draft.mountPath}
            />
          </Field>

          <Field htmlFor="output-visibility" label="Visibility">
            <Select
              id="output-visibility"
              onChange={(event) => {
                set("visibility", event.target.value);
              }}
              value={draft.visibility}
            >
              <option value="">Baseline</option>
              <option value="private">Private</option>
              <option value="public">Public</option>
            </Select>
          </Field>

          <Field htmlFor="output-host" label="Broker host">
            <TextInput
              id="output-host"
              onChange={(event) => {
                set("host", event.target.value);
              }}
              placeholder="baseline"
              value={draft.host}
            />
          </Field>

          <Field htmlFor="output-port" label="Broker port">
            <NumberInput
              id="output-port"
              max={65_535}
              min={1}
              onChange={(event) => {
                set("port", event.target.value);
              }}
              placeholder="baseline"
              value={draft.port}
            />
          </Field>
        </div>

        <Field
          hint="The public URL listeners use."
          htmlFor="output-published"
          label="Published address"
        >
          <TextInput
            id="output-published"
            onChange={(event) => {
              set("publishedUrl", event.target.value);
            }}
            placeholder="https://stream.example.com/late-night.mp3"
            value={draft.publishedUrl}
          />
        </Field>

        <Switch
          checked={draft.enabled}
          description="Off: saved but not published."
          label="Enabled"
          onChange={(next) => {
            set("enabled", next);
          }}
        />

        {error ? <Notice tone="danger">{error}</Notice> : null}

        <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button disabled={busy} type="submit">
            {busy ? "Saving…" : editing ? "Save delivery" : "Add delivery"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
